import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const thumbs = [
  { id: "AX-01", title: "AUTONOMOUS RESEARCH AGENT", stack: "TS · PYTHON · LLM", year: "2025" },
  { id: "AX-02", title: "MULTI-TENANT INFERENCE STACK", stack: "GO · CUDA · POSTGRES", year: "2025" },
  { id: "AX-03", title: "EVAL / TELEMETRY PIPELINE", stack: "RUST · CLICKHOUSE", year: "2024" },
  { id: "AX-04", title: "REALTIME EDITOR / PAIR MODE", stack: "REACT · WEBSOCKET", year: "2024" },
  { id: "AX-05", title: "AGENT ORCHESTRATOR CLI", stack: "PYTHON · OPENAI", year: "2024" },
  { id: "AX-06", title: "VECTOR MEMORY LAYER", stack: "TS · PGVECTOR", year: "2023" },
];

const snippets = [
  "const agent = spawn({ role: 'planner' })",
  "await agent.execute(plan, { verify: true })",
  "if (result.ok) ship() else iterate()",
  "const eval = telemetry.window('7d')",
  "queue.commit(next).then(deploy)",
];

export default function SceneShips() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      // set initial hidden state for the digits that come in later
      gsap.set([".digit-comma", ".digit-0a", ".digit-0b", ".digit-0c"], {
        opacity: 0,
        xPercent: -30,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "+=320%",
          scrub: 1,
          pin: true,
        },
      });

      // ── Phase 1: number reveals 1 → 1, → 1,0 → 1,00 → 1,000 ─────────
      tl.to(".digit-comma", { opacity: 1, xPercent: 0, duration: 0.4 }, 0.3)
        .to(".digit-0a",    { opacity: 1, xPercent: 0, duration: 0.4 }, 0.55)
        .to(".digit-0b",    { opacity: 1, xPercent: 0, duration: 0.4 }, 0.8)
        .to(".digit-0c",    { opacity: 1, xPercent: 0, duration: 0.4 }, 1.05);

      // ── Phase 2: horizontal caravan of thumbs (vertical scroll drives horizontal) ─
      tl.fromTo(
        ".ships-track",
        { xPercent: 0 },
        { xPercent: -62, duration: 3, ease: "none" },
        1.3
      );

      // code snippets fade in as a stacked side-column
      tl.from(".ships-snippet", {
        opacity: 0,
        y: 12,
        stagger: 0.08,
        duration: 0.35,
      }, 1.4);

      // copy items reveal
      tl.from(".ships-copy > *", {
        yPercent: 60,
        opacity: 0,
        stagger: 0.08,
        duration: 0.35,
      }, 2.0);

      // ── Phase 3: final "0" expands as mask into next scene ──────────
      tl.to([".digit-1", ".digit-comma", ".digit-0a", ".digit-0b", ".ships-track", ".ships-snippet-col", ".ships-copy", ".ships-meta-top", ".ships-meta-bottom"], {
        opacity: 0,
        duration: 0.4,
      }, 3.4);

      tl.to(".digit-0c", {
        scale: 14,
        transformOrigin: "50% 50%",
        duration: 1.0,
        ease: "power3.inOut",
      }, 3.4);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="scene-ships"
      ref={root}
      data-theme="paper"
      className="relative h-screen w-full overflow-hidden paper-bg grain-dark"
    >
      {/* ── Top meta strip (below fixed nav) ── */}
      <div className="ships-meta-top absolute top-20 md:top-24 left-0 right-0 px-6 md:px-10 flex items-start justify-between pointer-events-none z-20">
        <div className="type-label text-[color:var(--ink)]/70">
          <div>CHAPTER · 03</div>
          <div className="mt-1 text-[color:var(--ink)]">ONE THOUSAND SHIPS</div>
        </div>
        <div className="type-label text-[color:var(--ink)]/70 text-right">
          <div>PIN · ACTIVE</div>
          <div className="mt-1 text-[color:var(--ink)]">SPATIAL COUNT</div>
        </div>
      </div>

      {/* ── HUGE NUMBER — the scene's architecture ── */}
      <div
        className="ships-content absolute inset-0 flex items-center justify-center type-display text-[color:var(--ink)] pointer-events-none select-none z-10"
        style={{
          fontSize: "clamp(180px, 22vw, 340px)",
          lineHeight: 0.85,
          letterSpacing: "-0.04em",
          fontWeight: 900,
        }}
      >
        <span className="digit-1 inline-block">1</span>
        <span className="digit-comma inline-block">,</span>
        <span className="digit-0a inline-block">0</span>
        <span className="digit-0b inline-block">0</span>
        <span className="digit-0c inline-block">0</span>
      </div>

      {/* ── Horizontal caravan of project thumbs (below the number) ── */}
      <div className="absolute left-0 right-0 bottom-24 md:bottom-28 overflow-hidden pointer-events-none z-0">
        <div
          className="ships-track flex gap-6 md:gap-8 pl-[10vw]"
          style={{ willChange: "transform", width: "max-content" }}
        >
          {thumbs.map((t) => (
            <div
              key={t.id}
              className="shrink-0 w-[22vw] min-w-[220px] h-[22vh] min-h-[180px] border border-[color:var(--ink)]/40 bg-[color:var(--paper)] relative overflow-hidden"
            >
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(90deg, rgba(9,10,10,0.06) 1px, transparent 1px), linear-gradient(rgba(9,10,10,0.06) 1px, transparent 1px)",
                  backgroundSize: "20px 20px",
                }}
              />
              <div className="relative p-3 md:p-4 h-full flex flex-col justify-between type-mono text-[10px] text-[color:var(--ink)]">
                <div className="flex justify-between">
                  <span className="opacity-70">{t.id}</span>
                  <span>SHIPPED · {t.year}</span>
                </div>
                <div>
                  <div className="type-display text-[color:var(--ink)] text-sm md:text-base leading-tight">
                    {t.title}
                  </div>
                  <div className="mt-1.5 opacity-60">{t.stack}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Code snippets in a dedicated LEFT column (not floating over cards) ── */}
      <div className="ships-snippet-col absolute top-40 md:top-48 left-6 md:left-10 max-w-[36vw] z-20 pointer-events-none">
        <div className="type-label text-[color:var(--ink)]/50 mb-3">// EXECUTION LOG</div>
        <div className="space-y-2">
          {snippets.map((s, i) => (
            <div
              key={i}
              className="ships-snippet type-mono text-[11px] md:text-[12px] text-[color:var(--ink)]/70 flex gap-3"
            >
              <span className="opacity-40 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Copy stack on the right ── */}
      <div className="ships-copy absolute top-40 md:top-48 right-6 md:right-10 type-mono text-[color:var(--ink)] text-right space-y-1.5 text-xs md:text-sm z-20 pointer-events-none">
        <div className="type-label text-[color:var(--ink)]/50 mb-3">// CAPACITY</div>
        <div>— ONE BUILDER</div>
        <div>— AGENT-POWERED SCALE</div>
        <div>— FULL STACK CONTROL</div>
        <div>— NO ARTIFICIAL LIMITS</div>
      </div>

      {/* ── Bottom meta ── */}
      <div className="ships-meta-bottom absolute bottom-6 left-0 right-0 px-6 md:px-10 flex items-end justify-between pointer-events-none z-20">
        <div className="type-label text-[color:var(--ink)]/70">SCENE / 03</div>
        <div className="type-label text-[color:var(--ink)]/70">PIN · HORIZONTAL SCROLL</div>
      </div>
    </section>
  );
}